import _ from 'lodash';

import {
  HistoryTypeToPendingChangesTypeMap,
  PendingChangesActionType,
} from 'constants/pendingChanges';
import { getPersonFullName } from 'utils/loanApplication';

// Humanize a camelCase / PascalCase / snake_case property key into Title-Case
// words ("anticipatedClosingDate" -> "Anticipated Closing Date"), preserving
// all-caps acronyms (REO, MI).
const humanizePropertyName = key =>
  String(key ?? '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map(word =>
      word === word.toUpperCase()
        ? word
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    )
    .join(' ');

// A staged change is worth showing only when it actually changes a value. Mirrors
// the loan-editor's "valuable change" rule so a no-op (old === new, or a
// null<->false toggle) is not surfaced as something to review.
const isValuableChange = ({ oldValue, newValue }) => {
  if (oldValue === newValue) {
    return false;
  }

  if (_.isNull(oldValue) && newValue === false) {
    return false;
  }

  if (_.isNull(newValue) && oldValue === false) {
    return false;
  }

  return true;
};

// Map the loan's flattened staged-change rows onto the shape PendingChangesCard
// renders. Intentionally light — it does NOT pull the loan-editor's full field-
// config normalizer (which drags the FormGenerator/FormEditor chain in); the
// confirmation only needs a readable property label, the before/after values,
// the author, and the AI-attribution fields.
export const buildStagedChangeCards = rawChanges =>
  (rawChanges ?? []).filter(isValuableChange).map((change, index) => ({
    id: change.id ?? `${change.entityPrimaryKey ?? 'change'}-${change.propertyName ?? index}`,
    entityPrimaryKey: change.entityPrimaryKey ?? null,
    propertyName: humanizePropertyName(change.propertyName),
    oldValue: change.oldValue ?? null,
    newValue: change.newValue ?? null,
    changeType:
      HistoryTypeToPendingChangesTypeMap[change.actionType] ?? PendingChangesActionType.MODIFIED,
    formattedFullName: getPersonFullName(change.createdByUser || {}) || null,
    modifiedOn: change.createdDate ?? null,
    actorType: change.actorType ?? null,
    aiProcessId: change.aiProcessId ?? null,
    aiProcessExecutionId: change.aiProcessExecutionId ?? null,
  }));
